import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Plus,
  Upload,
  Search,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Mail,
  Filter,
  Eye,
  X,
  Globe,
  Database,
  Layers,
  Settings,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';
import { DeletionProgressPopup } from '../../components/common/DeletionProgressPopup';
import {
  ModuleCatalogItem,
  loadModuleCatalog,
  getModuleSelectionMap,
  applyCompanyRolePolicies,
  applyCompanySubscriptions,
} from '../../utils/moduleAccessAdmin';
import {
  getFallbackOnboardingSchema,
  isDemoTemplateRow,
  OnboardingSchemaMap,
  parseBooleanCell,
  parseExcelRows,
  parseNumberCell,
  pickRowValue,
  validateOnboardingRow,
} from '../../utils/adminBulkOnboarding';

interface Company {
  company_id: number;
  company_name: string;
  company_code: string;
  industry_type?: string | null;
  country?: string | null;
  location?: string | null;
  timezone?: string | null;
  currency?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | string[] | null;
  primary_contact_phones?: string[];
  subscription_plan?: string | null;
  max_sites: number;
  max_users: number;
  storage_limit_gb: number;
  is_active: boolean;
  is_trial: boolean;
  database_name?: string | null;
  database_created: boolean;
  provisioning_status: string;
  logo_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

const CompanyManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [viewingCompany, setViewingCompany] = useState<Company | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [deleteStreamUrl, setDeleteStreamUrl] = useState<string | null>(null);
  const [isManualCode, setIsManualCode] = useState(false);
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogItem[]>([]);
  const [companyModuleSelection, setCompanyModuleSelection] = useState<Record<string, boolean>>({});
  const [roleIdsForPolicies, setRoleIdsForPolicies] = useState<number[]>([]);
  const [moduleDurationMonths, setModuleDurationMonths] = useState<number>(12);
  const [applyCompanyModulePolicy, setApplyCompanyModulePolicy] = useState(true);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>(['']);
  const [isUploadingCompanies, setIsUploadingCompanies] = useState(false);
  const [onboardingSchema, setOnboardingSchema] = useState<Partial<OnboardingSchemaMap>>(getFallbackOnboardingSchema());
  const [dropdowns, setDropdowns] = useState<Record<string, Array<{ value: string; label: string }>>>({
    industries: [],
    countries: [],
    timezones: [],
    currencies: [],
    subscription_plans: [],
  });
  const [shouldAssignTokens, setShouldAssignTokens] = useState(true);
  const [tabTokens, setTabTokens] = useState({
    download_raw_data: '200',
    generate_alerts: '200',
    run_rtc_optimization: '100',
  });
  const [feedbackToast, setFeedbackToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setFeedbackToast({ type, message });
    setTimeout(() => setFeedbackToast(null), 4000);
  };
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const companyUploadInputRef = useRef<HTMLInputElement | null>(null);
  const POLL_INTERVAL = 30000; // 30s scalable polling (prevents DB connection exhaustion)
  const roleName = String((currentUser as any)?.roleName || currentUser?.role || '').toLowerCase();
  const isSystemAdmin =
    !!currentUser?.isGlobalAdmin ||
    !!(currentUser as any)?.is_superuser ||
    !!(currentUser as any)?.is_super_admin ||
    ['system_admin', 'super_admin'].includes(roleName);

  // Strictly prevent HO Admins and Site Admins from managing customers
  const canManageCustomers = isSystemAdmin;

  const normalizeIndustryType = (value?: string | null): string => {
    const text = String(value || '').trim();
    const map: Record<string, string> = {
      energy: 'Renewable Energy',
      manufacturing: 'Manufacturing',
      it: 'Information Technology',
      logistics: 'Logistics',
      'renewable energy': 'Renewable Energy',
      'information technology': 'Information Technology',
    };
    return map[text.toLowerCase()] || (text || 'Renewable Energy');
  };

  const [newCompany, setNewCompany] = useState({
    company_name: '',
    company_code: '',
    industry_type: 'Renewable Energy',
    country: 'India',
    location: '',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    subscription_plan: 'basic',
    max_sites: 5,
    max_users: 10,
    storage_limit_gb: 10,
    logo_url: '',
    is_active: true,
    is_trial: true,
  });

  // Initial load (System Admin only)

  const normalizeSubscriptionPlan = (value?: string | null): string => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if (!normalizedValue) {
      return 'basic';
    }

    const allowedPlans = (
      dropdowns.subscription_plans.length > 0
        ? dropdowns.subscription_plans.map((item) => item.value)
        :
      onboardingSchema?.companies?.fields?.find((field) => field.key === 'subscription_plan')?.allowed_values ||
      ['basic', 'premium', 'enterprise']
    ).map((item) => String(item).trim());

    const matchedPlan = allowedPlans.find((plan) => plan.toLowerCase() === normalizedValue);
    return matchedPlan || 'basic';
  };
  useEffect(() => {
    if (!canManageCustomers) {
      setLoading(false);
      return;
    }

    void fetchCompanies(true);
  }, [canManageCustomers]);

  useEffect(() => {
    if (!canManageCustomers) {
      return;
    }

    const loadModuleAccessMeta = async () => {
      try {
        const [modules, rolesResponse] = await Promise.all([
          loadModuleCatalog(backendApi),
          backendApi.getRoles(),
        ]);

        setModuleCatalog(modules);
        setCompanyModuleSelection(getModuleSelectionMap(modules));

        const roles = Array.isArray(rolesResponse?.roles) ? rolesResponse.roles : [];
        const targetRoleIds = roles
          .filter((role: any) => ['ho_admin', 'site_admin', 'general_user'].includes(String(role?.role_name || '').toLowerCase()))
          .map((role: any) => Number(role?.role_id))
          .filter((roleId: number) => Number.isFinite(roleId) && roleId > 0);
        setRoleIdsForPolicies(targetRoleIds);
      } catch (error) {
        console.error('Failed to load module access metadata:', error);
      }
    };

    void loadModuleAccessMeta();
  }, [canManageCustomers]);

  useEffect(() => {
    if (!canManageCustomers) {
      return;
    }

    const loadDropdowns = async () => {
      const response = await backendApi.getDropdowns();
      if (response.success && response.dropdowns) {
        setDropdowns({
          industries: response.dropdowns.industries || [],
          countries: response.dropdowns.countries || [],
          timezones: response.dropdowns.timezones || [],
          currencies: response.dropdowns.currencies || [],
          subscription_plans: response.dropdowns.subscription_plans || [],
        });
      }
    };

    void loadDropdowns();

    const loadOnboardingSchema = async () => {
      const response = await backendApi.getAdminOnboardingSchema();
      if (response.success && response.schema) {
        setOnboardingSchema(response.schema);
      }
    };

    void loadOnboardingSchema();
  }, [canManageCustomers]);

  // Real-time polling
  useEffect(() => {
    if (!canManageCustomers) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    startRealTimePolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [canManageCustomers]);

  const fetchCompanies = async (showLoading = false) => {
    if (!canManageCustomers) {
      setLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await backendApi.getCompanies();
      if (response.success) {
        setCompanies(response.companies || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const startRealTimePolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      fetchCompanies(false);
    }, POLL_INTERVAL);
  };

  const handleAddCompany = async () => {
    if (!canManageCustomers) {
      alert('Only Super Admin can create customer companies');
      return;
    }
    if (!newCompany.company_name.trim() || !newCompany.company_code.trim()) {
      alert('Company name and code are required');
      return;
    }

    const normalizedPhones = phoneNumbers
      .map((phone) => phone.trim())
      .filter((phone) => phone.length > 0);
    if (normalizedPhones.length > 25) {
      alert('Please keep up to 25 phone numbers.');
      return;
    }
    const payload = {
      ...newCompany,
      primary_contact_phone: normalizedPhones,
    };

    try {
      const response = await backendApi.createCompany(payload);
      if (response.success) {
        let createdCompanyId = Number(response.company?.company_id || response.company?.id);
        if (!createdCompanyId) {
          const refreshResponse = await backendApi.getCompanies();
          const matchedCompany = (refreshResponse?.companies || []).find(
            (company: any) =>
              String(company?.company_code || '').toUpperCase() === String(newCompany.company_code || '').toUpperCase()
          );
          createdCompanyId = Number(matchedCompany?.company_id || matchedCompany?.id || 0);
        }

        if (applyCompanyModulePolicy && createdCompanyId > 0 && Object.keys(companyModuleSelection).length > 0) {
          await applyCompanyRolePolicies(backendApi, {
            companyId: createdCompanyId,
            moduleSelections: companyModuleSelection,
            roleIds: roleIdsForPolicies,
          });

          await applyCompanySubscriptions(backendApi, {
            companyId: createdCompanyId,
            moduleSelections: companyModuleSelection,
            durationMonths: moduleDurationMonths,
          });
        }

        // Assign initial tokens to HO Admin user if enabled
        if (shouldAssignTokens && createdCompanyId > 0) {
          try {
            // Calculate total tokens from tab-specific allocations
            const total = 
              (parseInt(tabTokens.download_raw_data) || 0) +
              (parseInt(tabTokens.generate_alerts) || 0) +
              (parseInt(tabTokens.run_rtc_optimization) || 0);

            if (total > 0) {
              // Company-level issue: if HO exists, backend maps immediately;
              // if HO not created yet, backend keeps tokens pending for this company.
              const tokenAssignResponse = await backendApi.adminAssignCompanyTokens({
                company_id: createdCompanyId,
                tokens: total,
                remarks: 'Initial company token issue from customer registration form',
                tab_token_breakdown: {
                  download_raw_data: parseInt(tabTokens.download_raw_data) || 0,
                  generate_alerts: parseInt(tabTokens.generate_alerts) || 0,
                  run_rtc_optimization: parseInt(tabTokens.run_rtc_optimization) || 0,
                },
              });

              if (!tokenAssignResponse?.success) {
                throw new Error(tokenAssignResponse?.error || 'Token assignment failed');
              }

              if (tokenAssignResponse?.data?.status === 'pending_ho_admin') {
                alert(`Company created. ${total} tokens issued on company name and kept pending. Create HO Admin to activate usage.`);
              }
            }
          } catch (tokenError) {
            console.error('Error assigning tokens:', tokenError);
            alert('Company created, but token assignment failed. Please assign tokens from Admin > Token Management.');
          }
        }

        await fetchCompanies(false);
        resetForm();
        setFormError(null);
        setShowAddModal(false);
        showToast('Company created successfully!', 'success');
      } else {
        setFormError(response.error || 'Failed to create company');
      }
    } catch (error: any) {
      setFormError(error.message || 'Failed to create company');
    }
  };

  const handleUpdateCompany = async () => {
    if (!canManageCustomers) {
      showToast('Only Super Admin can update customer companies', 'error');
      return;
    }
    if (!editingCompany) return;
    if (!newCompany.company_name.trim()) {
      setFormError('Company name is required');
      return;
    }

    const normalizedPhones = phoneNumbers
      .map((phone) => phone.trim())
      .filter((phone) => phone.length > 0);
    if (normalizedPhones.length > 25) {
      setFormError('Please keep up to 25 phone numbers.');
      return;
    }
    const payload = {
      ...newCompany,
      primary_contact_phone: normalizedPhones,
    };

    try {
      const response = await backendApi.updateCompany(editingCompany.company_id, payload);
      if (response.success) {
        if (applyCompanyModulePolicy && Object.keys(companyModuleSelection).length > 0) {
          await applyCompanyRolePolicies(backendApi, {
            companyId: editingCompany.company_id,
            moduleSelections: companyModuleSelection,
            roleIds: roleIdsForPolicies,
          });

          await applyCompanySubscriptions(backendApi, {
            companyId: editingCompany.company_id,
            moduleSelections: companyModuleSelection,
            durationMonths: moduleDurationMonths,
          });
        }

        await fetchCompanies(false);
        setEditingCompany(null);
        setShowAddModal(false);
        resetForm();
        setFormError(null);
        showToast('Company updated successfully!', 'success');
      } else {
        setFormError(response.error || 'Failed to update company');
      }
    } catch (error: any) {
      setFormError(error.message || 'Failed to update company');
    }
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!canManageCustomers) {
      alert('Only Super Admin can delete customer companies');
      return;
    }

    if (window.confirm('Are you sure you want to permanently delete this company and all associated sites, users, and module data? This action cannot be undone.')) {
      setDeleteStreamUrl(`${process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000'}/api/super-admin/companies/${companyId}/?stream=true&delete_database=true`);
    }
  };

  const handleProvision = async (companyId: number) => {
    if (!canManageCustomers) {
      alert('Only Super Admin can provision customer companies');
      return;
    }
    if (!window.confirm('This will provision the company database. Proceed?')) {
      return;
    }

    try {
      const response = await backendApi.provisionCompany(companyId);
      if (response.success) {
        alert('Database provisioned successfully!');
        await fetchCompanies(false);
      } else {
        alert(response.message || 'Provisioning failed');
      }
    } catch (error: any) {
      alert(error.message || 'Provisioning failed');
    }
  };

  const resetForm = () => {
    // 1. Calculate next CUST code first
    let nextCode = 'CUST001';
    if (companies.length > 0) {
      const compCodes = companies
        .map(c => c.company_code)
        .filter(code => /^CUST\d+$/.test(code))
        .map(code => parseInt(code.substring(4)));

      const nextNum = compCodes.length > 0 ? Math.max(...compCodes) + 1 : 1;
      nextCode = `CUST${String(nextNum).padStart(3, '0')}`;
    }

    // 2. Clear form with all required fields
    setNewCompany({
      company_name: '',
      company_code: nextCode,
      industry_type: 'Renewable Energy',
      country: 'India',
      location: '',
      timezone: 'Asia/Kolkata',
      currency: 'INR',
      primary_contact_name: '',
      primary_contact_email: '',
      primary_contact_phone: '',
      subscription_plan: 'basic',
      max_sites: 5,
      max_users: 10,
      storage_limit_gb: 10,
      logo_url: '',
      is_active: true,
      is_trial: true,
    });
    setPhoneNumbers(['']);
    setIsManualCode(false);
    setShouldAssignTokens(true);
    setTabTokens({
      download_raw_data: '200',
      generate_alerts: '200',
      run_rtc_optimization: '100',
    });
    if (moduleCatalog.length > 0) {
      setCompanyModuleSelection(getModuleSelectionMap(moduleCatalog));
    }
    setApplyCompanyModulePolicy(true);
    setModuleDurationMonths(12);
  };

  const parsePhoneNumbers = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item || '').trim())
        .filter((item) => item.length > 0);
    }

    const raw = String(value || '').trim();
    if (!raw) {
      return [];
    }

    return raw
      .split(/[,;/|]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const handleCompanyUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canManageCustomers) {
      alert('Only Super Admin can bulk onboard customer companies');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploadingCompanies(true);
    try {
      const rows = await parseExcelRows(file);
      if (rows.length === 0) {
        alert('No data rows found in uploaded Excel file.');
        return;
      }

      const failures: string[] = [];
      let successCount = 0;
      let actionableRows = 0;

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const rowNumber = index + 2;

        if (isDemoTemplateRow('companies', row, onboardingSchema)) {
          continue;
        }

        actionableRows += 1;

        const schemaErrors = validateOnboardingRow('companies', row, onboardingSchema);
        if (schemaErrors.length > 0) {
          failures.push(`Row ${rowNumber}: ${schemaErrors.join('; ')}`);
          continue;
        }

        const companyName = String(pickRowValue(row, ['company_name', 'name'])).trim();
        const companyCode = String(pickRowValue(row, ['company_code', 'code'])).trim().toUpperCase();

        if (!companyName) {
          failures.push(`Row ${rowNumber}: company_name is required`);
          continue;
        }

        const payload: Record<string, any> = {
          company_name: companyName,
          industry_type: String(pickRowValue(row, ['industry_type'])).trim() || 'Renewable Energy',
          country: String(pickRowValue(row, ['country'])).trim() || 'India',
          location: String(pickRowValue(row, ['location'])).trim(),
          timezone: String(pickRowValue(row, ['timezone'])).trim() || 'Asia/Kolkata',
          currency: String(pickRowValue(row, ['currency'])).trim() || 'INR',
          primary_contact_name: String(pickRowValue(row, ['primary_contact_name'])).trim(),
          primary_contact_email: String(pickRowValue(row, ['primary_contact_email'])).trim(),
          primary_contact_phone: parsePhoneNumbers(
            pickRowValue(row, ['primary_contact_phone', 'primary_contact_phones'])
          ),
          subscription_plan: normalizeSubscriptionPlan(String(pickRowValue(row, ['subscription_plan'])).trim()),
          max_sites: parseNumberCell(pickRowValue(row, ['max_sites']), 5) || 5,
          max_users: parseNumberCell(pickRowValue(row, ['max_users']), 10) || 10,
          storage_limit_gb: parseNumberCell(pickRowValue(row, ['storage_limit_gb']), 10) || 10,
          is_active: parseBooleanCell(pickRowValue(row, ['is_active']), true),
          is_trial: parseBooleanCell(pickRowValue(row, ['is_trial']), true),
        };

        // Backward-compatible support for old sheets that still include company_code.
        if (companyCode) {
          payload.company_code = companyCode;
        }

        const response = await backendApi.createCompany(payload);
        if (response.success) {
          successCount += 1;
        } else {
          failures.push(`Row ${rowNumber}: ${response.error || 'create failed'}`);
        }
      }

      await fetchCompanies(false);

      if (actionableRows === 0) {
        alert('No actionable rows found. Replace or delete the demo row in the template and upload again.');
        return;
      }

      const summary = [
        `Company upload complete.`,
        `Success: ${successCount}`,
        `Failed: ${failures.length}`,
      ];

      if (failures.length > 0) {
        summary.push('', 'First errors:', ...failures.slice(0, 6));
      }
      alert(summary.join('\n'));
    } catch (error: any) {
      alert(error?.message || 'Failed to process company upload file');
    } finally {
      setIsUploadingCompanies(false);
      event.target.value = '';
    }
  };

  const loadCompanyModuleSelections = async (companyId: number) => {
    try {
      const catalog = moduleCatalog.length > 0 ? moduleCatalog : await loadModuleCatalog(backendApi);
      if (moduleCatalog.length === 0 && catalog.length > 0) {
        setModuleCatalog(catalog);
      }

      const response = await backendApi.getNavigationSubscriptions(companyId);
      const subscriptions = Array.isArray(response?.subscriptions) ? response.subscriptions : [];

      const activeModuleKeys = new Set(
        subscriptions
          .filter((item: any) => String(item?.status || '').toLowerCase() === 'active')
          .map((item: any) => String(item?.module_key || '').trim())
          .filter((key: string) => key.length > 0)
      );

      const selectionMap: Record<string, boolean> = {};
      catalog.forEach((module) => {
        selectionMap[module.module_key] = activeModuleKeys.has(module.module_key);
      });

      // If no subscription row exists yet, keep all enabled as default behavior.
      if (subscriptions.length === 0) {
        setCompanyModuleSelection(getModuleSelectionMap(catalog));
      } else {
        setCompanyModuleSelection(selectionMap);
      }

      const firstWindow = subscriptions.find((item: any) => item?.start_at && item?.end_at);
      if (firstWindow?.start_at && firstWindow?.end_at) {
        const start = new Date(firstWindow.start_at);
        const end = new Date(firstWindow.end_at);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start) {
          const months = Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)));
          setModuleDurationMonths(months);
        }
      }
    } catch (error) {
      console.error('Failed to load company module selections:', error);
      if (moduleCatalog.length > 0) {
        setCompanyModuleSelection(getModuleSelectionMap(moduleCatalog));
      }
    }
  };

  const handleEditCompany = (company: Company) => {
    const parsedPhoneNumbers = (() => {
      if (Array.isArray(company.primary_contact_phones) && company.primary_contact_phones.length > 0) {
        return company.primary_contact_phones.map((value) => String(value).trim()).filter((value) => value.length > 0);
      }

      if (Array.isArray(company.primary_contact_phone)) {
        return company.primary_contact_phone.map((value) => String(value).trim()).filter((value) => value.length > 0);
      }

      const rawPhone = String(company.primary_contact_phone || '').trim();
      if (!rawPhone) {
        return [];
      }

      try {
        const parsed = JSON.parse(rawPhone);
        if (Array.isArray(parsed)) {
          return parsed.map((value) => String(value).trim()).filter((value) => value.length > 0);
        }
      } catch (error) {
        // Keep backward compatibility with legacy comma-separated phone string.
      }

      return rawPhone
        .split(/[,;/|]+/)
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
    })();

    setEditingCompany(company);
    setNewCompany({
      company_name: company.company_name,
      company_code: company.company_code,
      industry_type: normalizeIndustryType(company.industry_type),
      country: company.country || 'India',
      location: company.location || '',
      timezone: company.timezone || 'Asia/Kolkata',
      currency: company.currency || 'INR',
      primary_contact_name: company.primary_contact_name || '',
      primary_contact_email: company.primary_contact_email || '',
      primary_contact_phone: Array.isArray(company.primary_contact_phone)
        ? company.primary_contact_phone.join(', ')
        : (company.primary_contact_phone || ''),
      subscription_plan: company.subscription_plan || 'basic',
      max_sites: (company as any).max_sites || 5,
      max_users: (company as any).max_users || 10,
      storage_limit_gb: (company as any).storage_limit_gb || 10,
      logo_url: company.logo_url || '',
      is_active: company.is_active,
      is_trial: (company as any).is_trial ?? true,
    });
    setPhoneNumbers(parsedPhoneNumbers.length > 0 ? parsedPhoneNumbers : ['']);
    setIsManualCode(true);
    setApplyCompanyModulePolicy(true);
    void loadCompanyModuleSelections(company.company_id);
    setShowAddModal(true);
  };

  const handleViewCompany = (company: Company) => {
    navigate(`/admin/companies/${company.company_id}`);
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch =
      company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.company_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.primary_contact_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && company.is_active) ||
      (statusFilter === 'inactive' && !company.is_active);

    const matchesPlan = planFilter === 'all' ||
      company.subscription_plan?.toLowerCase() === planFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesPlan;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Customer administration</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Building2 size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Customer accounts in a clean operational row view.</h1>
                  <p className="admin-page-subtitle mt-2">Manage customer identity, provisioning state, subscription plan, and module policy from a calmer interface that is easier to scan.</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {canManageCustomers && (
                <>
                  <input
                    ref={companyUploadInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleCompanyUpload}
                  />
                  <button
                    onClick={() => companyUploadInputRef.current?.click()}
                    className="admin-button admin-button-secondary"
                    disabled={isUploadingCompanies}
                  >
                    <Upload size={16} />
                    {isUploadingCompanies ? 'Uploading...' : 'Upload Data'}
                  </button>
                </>
              )}
              {canManageCustomers && (
                <button
                  onClick={() => {
                    setEditingCompany(null);
                    resetForm();
                    setShowAddModal(true);
                  }}
                  className="admin-button admin-button-primary"
                >
                  <Plus size={18} />
                  Create customer
                </button>
              )}
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Customers</p>
              <p className="admin-stat-value">{companies.length}</p>
              <p className="admin-stat-hint">Total registered customer accounts</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Active</p>
              <p className="admin-stat-value">{companies.filter((company) => company.is_active).length}</p>
              <p className="admin-stat-hint">Customer environments currently enabled</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Trial</p>
              <p className="admin-stat-value">{companies.filter((company) => company.is_trial).length}</p>
              <p className="admin-stat-hint">Accounts still on trial access</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Provisioned</p>
              <p className="admin-stat-value">{companies.filter((company) => company.database_created).length}</p>
              <p className="admin-stat-hint">Dedicated databases already provisioned</p>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Filter customers</h2>
              <p className="admin-section-copy">Search by company name, code, location, or primary contact.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="flex flex-col gap-4">
              {/* Row 1: Search & Reset */}
              <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                <div className="admin-search-wrap w-full md:max-w-md">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search customers by name, code, or contact..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-input"
                  />
                </div>
                
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setPlanFilter('all');
                  }}
                  className="admin-button admin-button-ghost w-full md:w-auto xl:h-[46px] xl:px-4 xl:whitespace-nowrap"
                >
                  <Filter size={16} />
                  Reset filters
                </button>
              </div>

              {/* Row 2: Select Filters with clean labels */}
              <div className="flex flex-wrap gap-3 pt-3 border-t border-[#1C4D88]/5">
                <div className="w-full sm:w-auto min-w-[140px] flex-1">
                  <label className="block text-xs font-bold text-[#344B66] uppercase tracking-wider mb-1.5">
                    Status
                  </label>
                  <select
                    title="Status Filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="admin-select w-full"
                  >
                    <option value="all">All status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="w-full sm:w-auto min-w-[160px] flex-1">
                  <label className="block text-xs font-bold text-[#344B66] uppercase tracking-wider mb-1.5">
                    Subscription Plan
                  </label>
                  <select
                    title="Plan Filter"
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    className="admin-select w-full"
                  >
                    <option value="all">All plans</option>
                    {(dropdowns.subscription_plans.length > 0 ? dropdowns.subscription_plans : [
                      { value: 'basic', label: 'Basic' },
                      { value: 'premium', label: 'Premium' },
                      { value: 'enterprise', label: 'Enterprise' },
                    ]).map((plan) => (
                      <option key={plan.value} value={plan.value}>{plan.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Customer directory</h2>
              <p className="admin-section-copy">Every company is presented as a horizontal row with operational metadata, plan details, and quick actions.</p>
            </div>
          </div>
          <div className="admin-section-body">
            {filteredCompanies.length === 0 ? (
              <div className="admin-empty">No customers matched the current filters.</div>
            ) : (
              <div className="admin-row-list">
                {filteredCompanies.map((company, index) => (
                  <motion.div
                    key={company.company_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index, 8) * 0.04 }}
                    className={`admin-row ${company.is_active ? '' : 'opacity-90'}`}
                  >
                    <div className="admin-row-leading">
                      <div className="admin-row-icon overflow-hidden bg-white border flex items-center justify-center flex-shrink-0">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                          <Building2 size={18} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="admin-row-title truncate">{company.company_name}</p>
                        <p className="admin-row-subtitle truncate">{company.location || company.country || 'Location not configured'}</p>
                        <div className="admin-row-tags">
                          <span className="admin-tag">{company.company_code}</span>
                          {company.industry_type && <span className="admin-tag">{company.industry_type}</span>}
                          {company.subscription_plan && <span className="admin-tag capitalize">{company.subscription_plan}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="admin-row-meta">
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Primary contact</p>
                        <p className="admin-meta-value">{company.primary_contact_name || company.primary_contact_email || 'Not assigned'}</p>
                      </div>
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Capacity</p>
                        <p className="admin-meta-value">{company.max_sites} sites • {company.max_users} users</p>
                      </div>
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Provisioning</p>
                        <p className="admin-meta-value">{company.database_created ? 'Database ready' : company.provisioning_status || 'Pending'}</p>
                      </div>
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Status</p>
                        <p className="admin-meta-value">{company.is_active ? 'Active customer' : 'Inactive customer'}</p>
                      </div>
                    </div>

                    <div className="admin-row-actions">
                      <span className={`admin-pill ${company.is_active ? 'admin-pill-success' : 'admin-pill-danger'}`}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button onClick={() => handleViewCompany(company)} className="admin-icon-button" title="View customer" aria-label="View customer">
                        <Eye size={16} />
                      </button>
                      {canManageCustomers && (
                        <button onClick={() => handleEditCompany(company)} className="admin-icon-button" title="Edit customer" aria-label="Edit customer">
                          <Edit size={16} />
                        </button>
                      )}
                      {canManageCustomers && (
                        <button onClick={() => handleDeleteCompany(company.company_id)} className="admin-icon-button text-[#B5475D]" title="Delete customer" aria-label="Delete customer">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.section>

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {showAddModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 pt-10 sm:pt-14 pb-4 px-2 sm:px-4" onClick={() => setShowAddModal(false)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()} className="admin-modal-shell w-full max-w-6xl max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col">
                <div className="admin-modal-header flex items-start justify-between gap-4 pb-3">
                  <div>
                    <p className="admin-eyebrow">Customer workflow</p>
                    <h3 className="admin-modal-title text-[1.5rem] text-[#10213A] mt-1">{editingCompany ? 'Edit customer account' : 'Register a new customer'}</h3>
                  </div>
                  <button onClick={() => setShowAddModal(false)} className="admin-icon-button"><X size={18} /></button>
                </div>
                <div className="admin-modal-body flex-1 overflow-y-auto">
                  {formError && (
                    <div className="mb-4 flex items-center justify-between p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium animate-in fade-in duration-200">
                      <div className="flex items-center gap-2.5">
                        <AlertCircle size={18} className="text-red-500 shrink-0" />
                        <span>{formError}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormError(null)}
                        className="text-red-500 hover:text-red-700 text-xs font-bold ml-2"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                  <div className="space-y-3">
                    {/* Row 1: Identity */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Customer Name *</label>
                        <input
                          type="text"
                          value={newCompany.company_name}
                          onChange={e => setNewCompany({ ...newCompany, company_name: e.target.value })}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] text-[13px] text-[#10213A]"
                          placeholder="Acme Solar"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[14px]" style={{ color: '#0b1a2f', fontWeight: 900 }}>Customer Code *</label>
                          {isManualCode && !editingCompany && (
                            <button
                              onClick={() => {
                                setIsManualCode(false);
                                if (companies.length > 0) {
                                  const compCodes = companies
                                    .map(c => c.company_code)
                                    .filter(code => /^CUST\d+$/.test(code))
                                    .map(code => parseInt(code.substring(4)));

                                  const nextNum = compCodes.length > 0 ? Math.max(...compCodes) + 1 : 1;
                                  const nextCode = `CUST${String(nextNum).padStart(3, '0')}`;
                                  setNewCompany({ ...newCompany, company_code: nextCode });
                                } else {
                                  setNewCompany({ ...newCompany, company_code: 'CUST001' });
                                }
                              }}
                              className="text-[9px] text-[#0066A3] hover:underline font-semibold"
                            >
                              Auto
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          value={newCompany.company_code}
                          onChange={e => {
                            setIsManualCode(true);
                            setNewCompany({ ...newCompany, company_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') });
                          }}
                          className={`w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-[#0084CF] font-mono text-[13px] font-semibold ${!isManualCode ? 'bg-[#F2F8FC]/50 border-[#C2DFF1] text-[#0D2747]' : 'bg-white border-gray-300 text-[#10213A]'}`}
                          placeholder="ACME"
                          maxLength={10}
                        />
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Industry</label>
                        <select value={newCompany.industry_type} onChange={e => setNewCompany({ ...newCompany, industry_type: e.target.value })} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] bg-white">
                          {(dropdowns.industries.length > 0 ? dropdowns.industries : [
                            { value: 'Renewable Energy', label: 'Renewable Energy' },
                            { value: 'Manufacturing', label: 'Manufacturing' },
                            { value: 'Information Technology', label: 'Information Technology' },
                            { value: 'Logistics', label: 'Logistics' },
                          ]).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Country</label>
                        <select value={newCompany.country} onChange={e => setNewCompany({ ...newCompany, country: e.target.value })} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] bg-white text-[#10213A]">
                          {(dropdowns.countries.length > 0 ? dropdowns.countries : [{ value: 'India', label: 'India' }]).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Row 2: Config */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Timezone</label>
                        <select value={newCompany.timezone} onChange={e => setNewCompany({ ...newCompany, timezone: e.target.value })} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] bg-white text-[#10213A]">
                          {(dropdowns.timezones.length > 0 ? dropdowns.timezones : [{ value: 'Asia/Kolkata', label: 'Asia/Kolkata' }]).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Currency</label>
                        <select value={newCompany.currency} onChange={e => setNewCompany({ ...newCompany, currency: e.target.value })} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] bg-white text-[#10213A]">
                          {(dropdowns.currencies.length > 0 ? dropdowns.currencies : [{ value: 'INR', label: 'INR' }]).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Plan</label>
                        <select value={newCompany.subscription_plan} onChange={e => setNewCompany({ ...newCompany, subscription_plan: e.target.value })} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-[13px]">
                          {(dropdowns.subscription_plans.length > 0 ? dropdowns.subscription_plans : [
                            { value: 'basic', label: 'Basic' },
                            { value: 'premium', label: 'Premium' },
                            { value: 'enterprise', label: 'Enterprise' },
                          ]).map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>
                          <input type="checkbox" checked={newCompany.is_trial} onChange={e => setNewCompany({ ...newCompany, is_trial: e.target.checked })} className="w-4 h-4" />
                          Free Trial
                        </label>
                      </div>
                    </div>                    {/* Row 3: Limits & Logo */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Max Sites</label>
                        <input 
                          type="number" 
                          value={newCompany.max_sites} 
                          onChange={e => setNewCompany({ ...newCompany, max_sites: parseInt(e.target.value) || 0 })} 
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] text-[#10213A]" 
                        />
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Max Users</label>
                        <input 
                          type="number" 
                          value={newCompany.max_users} 
                          onChange={e => setNewCompany({ ...newCompany, max_users: parseInt(e.target.value) || 0 })} 
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] text-[#10213A]" 
                        />
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Storage Limit (GB)</label>
                        <input 
                          type="number" 
                          value={newCompany.storage_limit_gb} 
                          onChange={e => setNewCompany({ ...newCompany, storage_limit_gb: parseInt(e.target.value) || 0 })} 
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] text-[#10213A]" 
                        />
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Company Logo</label>
                        <div className="flex items-center gap-3">
                          <input 
                            type="file" 
                            accept="image/*" 
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setNewCompany(prev => ({
                                    ...prev,
                                    logo_url: reader.result as string
                                  }));
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="hidden"
                            id="logo-upload-input"
                          />
                          <label 
                            htmlFor="logo-upload-input" 
                            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors text-[13px] font-semibold text-gray-700"
                          >
                            <Upload size={14} />
                            {newCompany.logo_url ? "Change Logo" : "Upload Logo"}
                          </label>
                          {newCompany.logo_url && (
                            <div className="flex items-center gap-2 bg-gray-50 border rounded-lg p-1">
                              <img 
                                src={newCompany.logo_url} 
                                alt="Logo Preview" 
                                className="h-7 w-auto max-w-[80px] object-contain rounded" 
                              />
                              <button 
                                type="button" 
                                onClick={() => setNewCompany(prev => ({ ...prev, logo_url: '' }))} 
                                className="text-red-500 hover:text-red-700 text-xs p-1"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Primary Contact */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Contact Name</label>
                        <input type="text" value={newCompany.primary_contact_name} onChange={e => setNewCompany({ ...newCompany, primary_contact_name: e.target.value })} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] text-[#10213A]" placeholder="John Doe" />
                      </div>
                      <div>
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Email</label>
                        <input type="email" value={newCompany.primary_contact_email} onChange={e => setNewCompany({ ...newCompany, primary_contact_email: e.target.value })} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] text-[#10213A]" placeholder="john@acme.com" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[14px] mb-1" style={{ color: '#0b1a2f', fontWeight: 900 }}>Phone</label>
                        <div className="space-y-2">
                          {phoneNumbers.map((phone, index) => (
                            <div key={`phone-${index}`} className="flex items-center gap-2">
                              <input
                                type="tel"
                                value={phone}
                                onChange={e => {
                                  const updated = [...phoneNumbers];
                                  updated[index] = e.target.value;
                                  setPhoneNumbers(updated);
                                  setNewCompany({
                                    ...newCompany,
                                    primary_contact_phone: updated
                                      .map((value) => value.trim())
                                      .filter((value) => value.length > 0)
                                      .join(', '),
                                  });
                                }}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] text-[#10213A]"
                                placeholder={index === 0 ? '+91 9876543210' : 'Additional phone number'}
                              />
                              {phoneNumbers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = phoneNumbers.filter((_, phoneIndex) => phoneIndex !== index);
                                    setPhoneNumbers(updated);
                                    setNewCompany({
                                      ...newCompany,
                                      primary_contact_phone: updated
                                        .map((value) => value.trim())
                                        .filter((value) => value.length > 0)
                                        .join(', '),
                                    });
                                  }}
                                  className="px-2 py-1 text-xs font-semibold text-red-700 border border-red-200 rounded hover:bg-red-50"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setPhoneNumbers([...phoneNumbers, ''])}
                            className="px-2 py-1 text-xs font-semibold text-[#1C4D88] border border-[#C2DFF1] rounded hover:bg-[#F2F8FC]"
                          >
                            + Add Phone
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Module Policy */}
                    {moduleCatalog.length > 0 && (
                      <div className="admin-module-policy mt-1">
                        <div className="admin-module-policy-header">
                          <label className="admin-module-policy-toggle">
                            <input
                              type="checkbox"
                              checked={applyCompanyModulePolicy}
                              onChange={(e) => setApplyCompanyModulePolicy(e.target.checked)}
                              className="w-4 h-4"
                            />
                            Module access policy
                          </label>
                          <select
                            value={moduleDurationMonths}
                            onChange={(e) => setModuleDurationMonths(Number(e.target.value))}
                            className="admin-module-duration-select"
                          >
                            <option value={1}>1M</option>
                            <option value={3}>3M</option>
                            <option value={6}>6M</option>
                            <option value={12}>12M</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              const allEnabled: Record<string, boolean> = {};
                              moduleCatalog.forEach((module) => {
                                allEnabled[module.module_key] = true;
                              });
                              setCompanyModuleSelection(allEnabled);
                            }}
                            className="admin-module-policy-action admin-module-policy-action-enable ml-auto"
                          >
                            Enable All
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const allDisabled: Record<string, boolean> = {};
                              moduleCatalog.forEach((module) => {
                                allDisabled[module.module_key] = false;
                              });
                              setCompanyModuleSelection(allDisabled);
                            }}
                            className="admin-module-policy-action admin-module-policy-action-disable"
                          >
                            Disable All
                          </button>
                        </div>
                        <div className="admin-module-policy-grid">
                          {moduleCatalog.map((module) => (
                            <label key={module.module_key} className="admin-module-policy-item">
                              <input
                                type="checkbox"
                                checked={!!companyModuleSelection[module.module_key]}
                                onChange={(e) => {
                                  setCompanyModuleSelection((prev) => ({
                                    ...prev,
                                    [module.module_key]: e.target.checked,
                                  }));
                                }}
                                className="w-4 h-4"
                              />
                              <span className="admin-module-policy-name truncate">{module.module_name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Row 6: Initial Token Assignment */}
                    {!editingCompany && (
                      <div className="mt-1 p-3 rounded-lg border border-[#FFB81C]/20 bg-amber-50/30">
                        <div className="flex items-center gap-3 mb-3">
                          <label className="flex items-center gap-2 text-[14px]" style={{ color: '#0b1a2f', fontWeight: 900 }}>
                            <input
                              type="checkbox"
                              checked={shouldAssignTokens}
                              onChange={(e) => setShouldAssignTokens(e.target.checked)}
                              className="w-4 h-4"
                            />
                            Assign initial simulation tokens to HO Admin (per tab)
                          </label>
                        </div>
                        {shouldAssignTokens && (
                          <div className="space-y-3">
                            <div className="text-[12px] text-[#0F3B72] font-extrabold bg-[#F2F8FC] border border-[#C2DFF1] rounded-lg px-3 py-2">
                              Specify token allocation for each tab type below
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                <label className="block text-[13px] text-[#1E2F45] font-extrabold mb-1">Download Raw Data</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    step="10"
                                    value={tabTokens.download_raw_data}
                                    onChange={(e) => setTabTokens({ ...tabTokens, download_raw_data: e.target.value })}
                                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] text-[12px] text-[#10213A] font-semibold"
                                  />
                                  <span className="text-[12px] font-bold text-gray-700">tokens</span>
                                </div>
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                <label className="block text-[13px] text-[#1E2F45] font-extrabold mb-1">Generate Alerts</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    step="10"
                                    value={tabTokens.generate_alerts}
                                    onChange={(e) => setTabTokens({ ...tabTokens, generate_alerts: e.target.value })}
                                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] text-[12px] text-[#10213A] font-semibold"
                                  />
                                  <span className="text-[12px] font-bold text-gray-700">tokens</span>
                                </div>
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                <label className="block text-[13px] text-[#1E2F45] font-extrabold mb-1">RTC Optimization</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    step="10"
                                    value={tabTokens.run_rtc_optimization}
                                    onChange={(e) => setTabTokens({ ...tabTokens, run_rtc_optimization: e.target.value })}
                                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] text-[12px] text-[#10213A] font-semibold"
                                  />
                                  <span className="text-[12px] font-bold text-gray-700">tokens</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-[12px] text-[#1E2F45] font-extrabold bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                              Total: {(parseInt(tabTokens.download_raw_data) || 0) + (parseInt(tabTokens.generate_alerts) || 0) + (parseInt(tabTokens.run_rtc_optimization) || 0)} tokens
                            </div>
                            <p className="text-[11px] font-semibold text-gray-600">Tokens will be assigned to the HO Admin user after customer registration completes.</p>
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                </div>
                <div className="admin-modal-footer gap-3">
                  <button onClick={() => setShowAddModal(false)} className="admin-button admin-button-ghost text-[13px]">Cancel</button>
                  <button onClick={editingCompany ? handleUpdateCompany : handleAddCompany} className="admin-button admin-button-primary text-[13px]">
                    {editingCompany ? 'Save Changes' : 'Register & Provision'}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* View Modal - Simplified */}
        <AnimatePresence>
          {showViewModal && viewingCompany && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowViewModal(false)}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="admin-modal-shell w-full max-w-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="admin-modal-header flex items-start justify-between gap-4">
                  <div>
                    <p className="admin-eyebrow">Customer snapshot</p>
                    <h3 className="admin-modal-title text-[1.6rem] text-[#10213A] mt-2">{viewingCompany.company_name}</h3>
                    <p className="text-sm text-[#5B708B] mt-1">Customer ID: {viewingCompany.company_id}</p>
                  </div>
                  <button onClick={() => setShowViewModal(false)} className="admin-icon-button"><X size={18} /></button>
                </div>
                <div className="admin-modal-body grid grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div><p className="text-[10px] font-bold text-gray-400 uppercase">Customer Code</p><p className="font-bold">{viewingCompany.company_code}</p></div>
                    <div><p className="text-[10px] font-bold text-gray-400 uppercase">Industry / Country</p><p className="capitalize">{viewingCompany.industry_type} - {viewingCompany.country}</p></div>
                  </div>
                  <div className="space-y-4">
                    <div><p className="text-[10px] font-bold text-gray-400 uppercase">Primary Contact</p><p className="font-medium">{viewingCompany.primary_contact_name}</p></div>
                    <div><p className="text-[10px] font-bold text-gray-400 uppercase">Subscription Plan</p><p className="capitalize font-bold text-[#0066A3]">{viewingCompany.subscription_plan} {viewingCompany.is_trial ? '(Trial)' : ''}</p></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><p className="text-[10px] font-bold text-gray-400 uppercase">Max Sites</p><p className="font-bold">{viewingCompany.max_sites}</p></div>
                      <div><p className="text-[10px] font-bold text-gray-400 uppercase">Max Users</p><p className="font-bold">{viewingCompany.max_users}</p></div>
                    </div>

                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
      {/* Deletion Progress Popup */}
      <DeletionProgressPopup
        url={deleteStreamUrl || ''}
        isOpen={!!deleteStreamUrl}
        onClose={() => setDeleteStreamUrl(null)}
        onSuccess={() => fetchCompanies(false)}
      />

      {/* Floating Feedback Toast */}
      {feedbackToast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in slide-in-from-bottom-3 duration-200 text-white ${
          feedbackToast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
        }`}>
          {feedbackToast.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
          <span>{feedbackToast.message}</span>
          <button type="button" onClick={() => setFeedbackToast(null)} className="ml-2 text-white/80 hover:text-white">
            <X size={15} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
